import { useCallback, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react'
import { apiService } from '../../services/apiService'
import { useApi } from '../../hooks/useApi'
import { useDashboardTokens, type DashboardTokens } from '../../theme/dashboardTokens'
import type { PostureData } from '../../types'

/**
 * Lifecycle Command Center — posture panel (v2, Lane B / V8).
 *
 * The "how healthy is the estate right now" band, sourced entirely from
 * `/api/reports/posture` (V3 — data already exists). Three tiles, per the
 * 2-lens review:
 *   - **Credential health** — donut: healthy / expiring / expired.
 *   - **Ownership coverage** — donut: owned / unowned.
 *   - **SSO mode** — a **split-bar** (SAML vs OIDC vs other), NOT a donut: it's
 *     a composition split, not a health %, so a proportion bar reads honestly.
 *
 * a11y: each donut is `role="img"` with a full `aria-label` summary; the numeric
 * legend beside it is the text alternative (so no separate table toggle for a
 * 2–3 segment ring). Segments are never colour-only — every one is labelled with
 * text + count. Donut tracks use the fixed-contrast `--dv-ring-track` (V1).
 * Credential/ownership sub-counts deep-link to filtered lists; SSO sub-counts are
 * static counts for now (no SSO-mode filter exists yet — see backlog).
 */

interface PosturePanelProps {
  /** Optional heading rendered above the tiles. */
  heading?: string
}

export function PosturePanel({ heading }: PosturePanelProps) {
  const navigate = useNavigate()
  const tokens = useDashboardTokens()

  const fetchPosture = useCallback(() => apiService.getPosture(), [])
  const { data, loading, error } = useApi<PostureData>(fetchPosture)

  return (
    <section aria-label="Estate posture">
      {heading ? (
        <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--dv-text)', margin: '0 0 12px' }}>{heading}</h2>
      ) : null}

      {loading ? (
        <div style={{ padding: '24px 8px' }}>
          <Spinner size={SpinnerSize.medium} label="Loading posture…" labelPosition="right" ariaLive="polite" />
        </div>
      ) : error ? (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      ) : data ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
          <CredentialHealthTile data={data} tokens={tokens} navigate={navigate} />
          <OwnershipTile data={data} tokens={tokens} navigate={navigate} />
          <SsoModeTile data={data} tokens={tokens} />
        </div>
      ) : null}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiles
// ─────────────────────────────────────────────────────────────────────────────

type Navigate = ReturnType<typeof useNavigate>

function CredentialHealthTile({
  data,
  tokens,
  navigate,
}: {
  data: PostureData
  tokens: DashboardTokens
  navigate: Navigate
}) {
  const { healthy, expiring, expired, total, healthyPct } = data.credentialHealth
  const segments: Segment[] = [
    { key: 'healthy', label: 'Healthy', value: healthy, color: tokens.added },
    { key: 'expiring', label: 'Expiring', value: expiring, color: tokens.amber },
    { key: 'expired', label: 'Expired', value: expired, color: tokens.removed },
  ]
  const ariaLabel =
    `Credential health: ${healthyPct}% healthy. ` +
    `${healthy} healthy, ${expiring} expiring, ${expired} expired of ${total} credentials.`

  return (
    <Card title="Credential health" tokens={tokens}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <Donut
          segments={segments}
          total={total}
          centerValue={`${healthyPct}%`}
          centerLabel="healthy"
          tokens={tokens}
          ariaLabel={ariaLabel}
        />
        <Legend>
          <LegendRow tokens={tokens} color={tokens.added} label="Healthy" value={healthy} />
          <LegendRow
            tokens={tokens}
            color={tokens.amber}
            label="Expiring"
            value={expiring}
            onClick={expiring > 0 ? () => navigate('/credentials?expiring=30') : undefined}
          />
          <LegendRow
            tokens={tokens}
            color={tokens.removed}
            label="Expired"
            value={expired}
            onClick={expired > 0 ? () => navigate('/credentials?includeExpired=true') : undefined}
          />
        </Legend>
      </div>
    </Card>
  )
}

function OwnershipTile({
  data,
  tokens,
  navigate,
}: {
  data: PostureData
  tokens: DashboardTokens
  navigate: Navigate
}) {
  const { withOwner, unowned, coveragePct } = data.ownership
  const total = withOwner + unowned
  const segments: Segment[] = [
    { key: 'owned', label: 'Owned', value: withOwner, color: tokens.added },
    { key: 'unowned', label: 'Unowned', value: unowned, color: tokens.amber },
  ]
  const ariaLabel =
    `Ownership coverage: ${coveragePct}% owned. ${withOwner} owned, ${unowned} unowned of ${total} applications.`

  return (
    <Card title="Ownership coverage" tokens={tokens}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <Donut
          segments={segments}
          total={total}
          centerValue={`${coveragePct}%`}
          centerLabel="owned"
          tokens={tokens}
          ariaLabel={ariaLabel}
        />
        <Legend>
          <LegendRow tokens={tokens} color={tokens.added} label="Owned" value={withOwner} />
          <LegendRow
            tokens={tokens}
            color={tokens.amber}
            label="Unowned"
            value={unowned}
            onClick={unowned > 0 ? () => navigate('/applications?hasOwner=false') : undefined}
          />
        </Legend>
      </div>
    </Card>
  )
}

function SsoModeTile({ data, tokens }: { data: PostureData; tokens: DashboardTokens }) {
  const navigate = useNavigate()
  const { saml, oidc, other, total, samlPct, oidcPct } = data.ssoSplit
  const otherPct = total > 0 ? Math.max(0, 100 - samlPct - oidcPct) : 0
  const segments: Segment[] = [
    { key: 'saml', label: 'SAML', value: saml, color: tokens.changed },
    { key: 'oidc', label: 'OIDC', value: oidc, color: tokens.cred },
    { key: 'other', label: 'Other', value: other, color: tokens.muted },
  ]
  const ariaLabel = `SSO mode split of ${total} applications: ${saml} SAML (${samlPct}%), ${oidc} OIDC (${oidcPct}%), ${other} other (${otherPct}%).`

  return (
    <Card title="SSO mode" tokens={tokens}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', height: '100%' }}>
        <SplitBar segments={segments} total={total} tokens={tokens} ariaLabel={ariaLabel} />
        <Legend horizontal>
          <LegendRow tokens={tokens} color={tokens.changed} label="SAML" value={saml} suffix={`${samlPct}%`}
            onClick={saml > 0 ? () => navigate('/applications?ssoMode=saml') : undefined} />
          <LegendRow tokens={tokens} color={tokens.cred} label="OIDC" value={oidc} suffix={`${oidcPct}%`}
            onClick={oidc > 0 ? () => navigate('/applications?ssoMode=oidc') : undefined} />
          {other > 0 ? <LegendRow tokens={tokens} color={tokens.muted} label="Other" value={other} /> : null}
        </Legend>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Primitives
// ─────────────────────────────────────────────────────────────────────────────

interface Segment {
  key: string
  label: string
  value: number
  color: string
}

function Card({ title, tokens, children }: { title: string; tokens: DashboardTokens; children: ReactNode }) {
  return (
    <div
      style={{
        flex: '1 1 260px',
        minWidth: 260,
        background: tokens.card2 || tokens.elevated,
        border: `1px solid ${tokens.line}`,
        borderRadius: 12,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: tokens.muted,
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

/**
 * Custom SVG donut. A full track ring (`--dv-ring-track`) with proportional
 * coloured arcs drawn via stroke-dasharray, starting at 12 o'clock. `role="img"`
 * + `aria-label` carry the meaning; the sibling legend carries the numbers.
 */
function Donut({
  segments,
  total,
  centerValue,
  centerLabel,
  tokens,
  ariaLabel,
}: {
  segments: Segment[]
  total: number
  centerValue: string
  centerLabel: string
  tokens: DashboardTokens
  ariaLabel: string
}) {
  const size = 108
  const r = 42
  const stroke = 13
  const c = 2 * Math.PI * r
  let acc = 0

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={ariaLabel} style={{ flex: '0 0 auto' }}>
      {/* Track */}
      <circle cx="50" cy="50" r={r} fill="none" stroke={tokens.ringTrack} strokeWidth={stroke} />
      {/* Segments */}
      <g transform="rotate(-90 50 50)">
        {total > 0 &&
          segments.map((s) => {
            if (s.value <= 0) return null
            const frac = s.value / total
            const dash = frac * c
            const offset = -acc * c
            acc += frac
            return (
              <circle
                key={s.key}
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={offset}
              />
            )
          })}
      </g>
      {/* Center label */}
      <text x="50" y="47" textAnchor="middle" style={{ fontSize: 20, fontWeight: 700, fill: tokens.text }}>
        {centerValue}
      </text>
      <text x="50" y="63" textAnchor="middle" style={{ fontSize: 9, fill: tokens.muted }}>
        {centerLabel}
      </text>
    </svg>
  )
}

/** Horizontal proportion bar for a composition split (SSO mode). */
function SplitBar({
  segments,
  total,
  tokens,
  ariaLabel,
}: {
  segments: Segment[]
  total: number
  tokens: DashboardTokens
  ariaLabel: string
}) {
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        height: 22,
        borderRadius: 6,
        overflow: 'hidden',
        background: tokens.ringTrack,
      }}
    >
      {total > 0 &&
        segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.key}
              style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
              title={`${s.label}: ${s.value}`}
            />
          ) : null,
        )}
    </div>
  )
}

function Legend({ children, horizontal }: { children: ReactNode; horizontal?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: horizontal ? 'row' : 'column',
        flexWrap: horizontal ? 'wrap' : 'nowrap',
        gap: horizontal ? 14 : 8,
      }}
    >
      {children}
    </div>
  )
}

function LegendRow({
  tokens,
  color,
  label,
  value,
  suffix,
  onClick,
}: {
  tokens: DashboardTokens
  color: string
  label: string
  value: number
  suffix?: string
  onClick?: () => void
}) {
  const swatch = <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flex: '0 0 auto' }} />
  const text = (
    <span style={{ fontSize: 13, color: tokens.slate }}>
      <span style={{ fontWeight: 700, color: tokens.text }}>{value}</span> {label}
      {suffix ? <span style={{ color: tokens.muted }}> · {suffix}</span> : ''}
    </span>
  )

  const rowStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: 'transparent',
    border: 'none',
    padding: onClick ? '2px 4px' : 0,
    margin: onClick ? '-2px -4px' : 0,
    borderRadius: 5,
    cursor: onClick ? 'pointer' : 'default',
    textAlign: 'left',
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-label={`${value} ${label}. View list.`} style={rowStyle}>
        {swatch}
        {text}
      </button>
    )
  }
  return (
    <span style={rowStyle}>
      {swatch}
      {text}
    </span>
  )
}

export default PosturePanel
